import type { ComponentTransform } from "building-types/components/componentTypes";
import type {
  FloorAssemblyOverride,
  FloorEdgeOverridesTable,
  HorizontalFloorAssemblyFreeForm,
  HorizontalFloorAssemblyRoomDerived,
} from "building-types/floors/floorAssemblyTypes";
import type { FloorBay, FloorSplitData, SplitLine } from "building-types/floors/floorSplitDataTypes";
import type { FloorSystemExternal } from "building-types/floors/floorSystemTypes";
import type {
  Foundation,
  FoundationEdgeOverridesTable,
  FoundationSystem,
} from "building-types/foundations/foundationSystemTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { RWTable, Table } from "common/data/table";
import type { Mutable } from "common/utils/types";
import {
  isHorizontalFloorAssemblyFreeForm,
  isHorizontalFloorAssemblyRoomDerived,
} from "doc/elements/floor/floorAssembly";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { evaluateEntityForComponentInstance } from "doc/formula/evalFormulas";
import { mat3Dot } from "geom/mat3";
import { polyLine2EdgeCount } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import {
  applyTransformToOutline,
  applyTransformToOutlineAndEdgeIds,
  componentInstanceMat3ForMirroredTemplate,
} from "../../utils/componentTransforms";
import { shouldGenerateEntity } from "../../utils/entityIsolation";
import { componentInstanceLevelId } from "./componentInstanceLevel";

function transformPolylinesAndEdgeIds(
  transform: ComponentTransform,
  polylines: PolyLine2[],
  edgeIds?: string[],
): { transformedPolylines: PolyLine2[]; transformedIds?: string[] } {
  if (edgeIds && polylines.length > 0) {
    const transformedPolylines: PolyLine2[] = [];
    const transformedIds: string[] = [];
    let edgeOffset = 0;
    for (const polyline of polylines) {
      const edgeCount = polyLine2EdgeCount(polyline);
      const polylineEdgeIds = edgeIds.slice(edgeOffset, edgeOffset + edgeCount);
      const { transformedOutline, transformedEdgeIds } = applyTransformToOutlineAndEdgeIds(
        transform,
        polyline,
        polylineEdgeIds,
      );
      transformedPolylines.push(transformedOutline);
      transformedIds.push(...transformedEdgeIds);
      edgeOffset += edgeCount;
    }
    return { transformedPolylines, transformedIds };
  }
  return {
    transformedPolylines: polylines.map((polyline) => applyTransformToOutline(transform.matrix, polyline)),
    transformedIds: edgeIds,
  };
}

function remapRoomScopedEdgeOverrideKeys(
  overrides: FloorEdgeOverridesTable | undefined,
  componentInstanceChain: readonly string[],
): FloorEdgeOverridesTable | undefined {
  if (!overrides) {
    return overrides;
  }
  const remapped: Mutable<FloorEdgeOverridesTable> = {};
  // This colon format is used in the doc for room-edge-scoped overrides
  // It should be changed in BDG-2668
  for (const key in overrides) {
    const colonIndex = key.indexOf(":");
    if (colonIndex === -1) {
      remapped[key] = overrides[key];
      continue;
    }
    const roomId = key.slice(0, colonIndex);
    const edgeSuffix = key.slice(colonIndex);
    remapped[`${instanceIdForComponent(roomId, componentInstanceChain)}${edgeSuffix}`] = overrides[key];
  }
  return remapped;
}

function transformPoint(point: Vec2, transform: ComponentTransform): Vec2 {
  // Because the way mat3Dot works is insane
  const [x, y] = mat3Dot(transform.matrix, point[0], point[1]);
  return [x, y];
}

function transformSplitData(
  splitData: FloorSplitData | undefined,
  transform: ComponentTransform,
): FloorSplitData | undefined {
  if (!splitData) {
    return splitData;
  }

  const splitLines: RWTable<SplitLine> = {};
  for (const splitLineId in splitData.splitLines) {
    const splitLine = splitData.splitLines[splitLineId];
    const { transformedOutline, transformedEdgeIds } = applyTransformToOutlineAndEdgeIds(
      transform,
      splitLine.polyline,
      splitLine.edgeIds,
    );
    splitLines[instanceIdForComponent(splitLineId, transform.componentInstanceChain)] = {
      ...splitLine,
      id: instanceIdForComponent(splitLineId, transform.componentInstanceChain),
      polyline: transformedOutline,
      edgeIds: transformedEdgeIds,
    };
  }

  const bays: RWTable<FloorBay> = {};
  for (const bayId in splitData.bays) {
    const bay = splitData.bays[bayId];
    bays[instanceIdForComponent(bayId, transform.componentInstanceChain)] = {
      ...bay,
      id: instanceIdForComponent(bayId, transform.componentInstanceChain),
      parentBayId: bay.parentBayId
        ? instanceIdForComponent(bay.parentBayId, transform.componentInstanceChain)
        : undefined,
      interiorPoint: transformPoint(bay.interiorPoint, transform),
      framingStartPt: bay.framingStartPt ? transformPoint(bay.framingStartPt, transform) : undefined,
    };
  }

  return { splitLines, bays };
}

function transformFreeFormFloor(
  floor: FloorAssemblyOverride & HorizontalFloorAssemblyFreeForm,
  transform: ComponentTransform,
): FloorAssemblyOverride {
  const { transformedPolylines: transformedPerimeters, transformedIds: edgeIds } = transformPolylinesAndEdgeIds(
    transform,
    floor.perimeters ?? [],
    floor.edgeIds,
  );
  const { transformedPolylines: transformedOpenings, transformedIds: openingEdgeIds } = transformPolylinesAndEdgeIds(
    transform,
    floor.openings ?? [],
    floor.openingEdgeIds,
  );

  return {
    ...floor,
    perimeters: transformedPerimeters,
    openings: transformedOpenings,
    edgeIds,
    openingEdgeIds,
    splitData: transformSplitData(floor.splitData, transform),
  };
}

function transformRoomDerivedFloor(
  floor: FloorAssemblyOverride & HorizontalFloorAssemblyRoomDerived,
  transform: ComponentTransform,
): FloorAssemblyOverride {
  const { componentInstanceChain } = transform;

  return {
    ...floor,
    roomIds: floor.roomIds?.map((roomId) => instanceIdForComponent(roomId, componentInstanceChain)),
    floorEdgeOverrides: remapRoomScopedEdgeOverrideKeys(floor.floorEdgeOverrides, componentInstanceChain),
    floorOpeningEdgeOverrides: remapRoomScopedEdgeOverrideKeys(floor.floorOpeningEdgeOverrides, componentInstanceChain),
    splitData: transformSplitData(floor.splitData, transform),
  };
}

function transformFloorForComponentInstance(
  floor: FloorAssemblyOverride,
  transform: ComponentTransform,
): FloorAssemblyOverride {
  if (isHorizontalFloorAssemblyFreeForm(floor)) {
    return transformFreeFormFloor(floor, transform);
  } else if (isHorizontalFloorAssemblyRoomDerived(floor)) {
    return transformRoomDerivedFloor(floor, transform);
  } else {
    return floor;
  }
}

/**
 * Re-keys `foundationEdgeOverrides` onto the instance's edge order.
 *
 * Overrides are keyed by an edge's 1-based position in the accompanying `edgeIds` array, so once the
 * instance transform reorders the edges the keys have to move with them -- otherwise an offset
 * authored on the last edge is read off whatever edge lands in that slot.
 */
function remapFoundationEdgeOverridesToPermutation(
  foundationEdgeOverrides: FoundationEdgeOverridesTable | undefined,
  permutation: number[],
): FoundationEdgeOverridesTable | undefined {
  if (!foundationEdgeOverrides) {
    return foundationEdgeOverrides;
  }
  const remapped: Mutable<FoundationEdgeOverridesTable> = {};
  permutation.forEach((sourceIndex, targetIndex) => {
    const override = foundationEdgeOverrides[sourceIndex + 1];
    if (override) {
      remapped[targetIndex + 1] = override;
    }
  });
  return remapped;
}

function transformFoundationForComponentInstance(foundation: Foundation, transform: ComponentTransform): Foundation {
  const { perimeter, edgeIds } = foundation;
  if (!perimeter || !edgeIds) {
    return foundation;
  }

  // Transform positional tokens rather than the edge ids themselves, so the reordering the
  // transform applies comes back as an explicit permutation that both the ids and the per-edge
  // overrides can be rebuilt from.
  const { transformedOutline, transformedEdgeIds } = applyTransformToOutlineAndEdgeIds(
    transform,
    perimeter,
    edgeIds.map((_edgeId, index) => `${index}`),
  );
  const permutation = transformedEdgeIds.map((token) => Number.parseInt(token, 10));

  return {
    ...foundation,
    perimeter: transformedOutline,
    edgeIds: permutation.map((sourceIndex) => edgeIds[sourceIndex]),
    foundationEdgeOverrides: remapFoundationEdgeOverridesToPermutation(foundation.foundationEdgeOverrides, permutation),
  };
}

// This is here because, unlike stairs and railings, foundations and floors can have no levelId, and just act
// like they're on the lowest level, and instances should keep that non-level.
function withRemappedLevelId<T extends { readonly levelId?: string }>(
  entity: T,
  levels: Table<Level>,
  transform: ComponentTransform,
): T {
  if (!entity.levelId) {
    return entity;
  }
  return {
    ...entity,
    levelId: componentInstanceLevelId(
      entity.levelId,
      levels,
      transform.verticalPlacement,
      transform.componentInstanceChain,
    ),
  };
}

function createHorizontalAssemblyInstances<
  T extends [FloorSystemExternal, FloorAssemblyOverride] | [FoundationSystem, Foundation],
>(
  docEntitySystems: Table<T[0]>,
  docEntities: Table<T[1]>,
  params: CreateHorizontalAssemblyInstancesParams,
  transformEntityForComponentInstance: (entity: T[1], transform: ComponentTransform) => T[1],
): { entitySystems: RWTable<T[0]>; entities: RWTable<T[1]> } {
  type EntitySystem = T[0];
  type Entity = T[1];
  const {
    idToParents,
    componentToTransforms,
    mirror,
    mirrorTranslationX,
    isolatedEntity,
    definitions,
    rawOptionChoices,
    levels,
  } = params;
  const entitySystems: RWTable<EntitySystem> = {};
  const entities: RWTable<Entity> = {};
  const entityIdsBySystemId: Record<string, string[]> = {};
  for (const entityId in docEntities) {
    const parentId = docEntities[entityId].parentId;
    if (parentId) {
      entityIdsBySystemId[parentId] ||= [];
      entityIdsBySystemId[parentId].push(entityId);
    }
  }
  for (const entitySystemId in docEntitySystems) {
    const parentComponents = idToParents[entitySystemId] ?? [];
    if (parentComponents.length === 0) {
      continue;
    }

    const rawEntitySystem = docEntitySystems[entitySystemId];
    for (const componentId of parentComponents) {
      for (const componentTransform of componentToTransforms[componentId] ?? []) {
        if (!shouldGenerateEntity(componentTransform.instanceId, idToParents, isolatedEntity)) {
          continue;
        }

        const transform = {
          ...componentTransform,
          matrix: componentInstanceMat3ForMirroredTemplate(mirror, mirrorTranslationX, componentTransform.matrix),
        };
        const { componentInstanceChain } = transform;
        const evaluatedEntitySystem = evaluateEntityForComponentInstance(
          rawEntitySystem,
          componentInstanceChain,
          definitions,
          rawOptionChoices,
        );
        if (evaluatedEntitySystem.disabled) {
          continue;
        }

        const instanceEntitySystemId = instanceIdForComponent(entitySystemId, componentInstanceChain);
        entitySystems[instanceEntitySystemId] = {
          ...evaluatedEntitySystem,
          id: instanceEntitySystemId,
        };

        for (const entityId of entityIdsBySystemId[entitySystemId] ?? []) {
          const evaluatedEntity = evaluateEntityForComponentInstance(
            docEntities[entityId],
            componentInstanceChain,
            definitions,
            rawOptionChoices,
          );
          if (evaluatedEntity.disabled) {
            continue;
          }

          const instanceEntityId = instanceIdForComponent(entityId, componentInstanceChain);
          const transformedEntity = transformEntityForComponentInstance(evaluatedEntity, transform);
          entities[instanceEntityId] = withRemappedLevelId(
            {
              ...transformedEntity,
              id: instanceEntityId,
              parentId: instanceEntitySystemId,
            },
            levels,
            transform,
          );
        }
      }
    }
  }
  return { entitySystems, entities };
}

interface CreateHorizontalAssemblyInstancesParams {
  idToParents: Table<readonly string[]>;
  componentToTransforms: Table<readonly ComponentTransform[]>;
  mirror: boolean;
  mirrorTranslationX: number;
  isolatedEntity: DocEntityHandle | undefined;
  definitions: OptionDefinitions;
  rawOptionChoices: OptionChoices;
  levels: Table<Level>;
}

export function createFoundationInstances(
  docFoundationSystems: Table<FoundationSystem>,
  docFoundations: Table<Foundation>,
  params: CreateHorizontalAssemblyInstancesParams,
): { instanceFoundationSystems: RWTable<FoundationSystem>; instanceFoundations: RWTable<Foundation> } {
  const { entitySystems: instanceFoundationSystems, entities: instanceFoundations } = createHorizontalAssemblyInstances<
    [FoundationSystem, Foundation]
  >(docFoundationSystems, docFoundations, params, transformFoundationForComponentInstance);
  return { instanceFoundationSystems, instanceFoundations };
}

export function createFloorInstances(
  docFloorSystems: Table<FloorSystemExternal>,
  docFloors: Table<FloorAssemblyOverride>,
  params: CreateHorizontalAssemblyInstancesParams,
): { instanceFloorSystems: RWTable<FloorSystemExternal>; instanceFloors: RWTable<FloorAssemblyOverride> } {
  const { entitySystems: instanceFloorSystems, entities: instanceFloors } = createHorizontalAssemblyInstances<
    [FloorSystemExternal, FloorAssemblyOverride]
  >(docFloorSystems, docFloors, params, transformFloorForComponentInstance);
  return { instanceFloorSystems, instanceFloors };
}
